import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './MultiSelect.module.css';

export interface MultiSelectOption {
  label: string;
  value: string;
}

export interface MultiSelectProps {
  id?: string;
  /** Texto del trigger cuando no hay selección. */
  placeholder?: string;
  /** Opciones a elegir (catálogo, se recibe por props). */
  options: MultiSelectOption[];
  /** Valores confirmados actualmente. */
  value: string[];
  /** Se dispara solo al presionar "Confirmar". */
  onChange: (value: string[]) => void;
  /** Texto del botón interno que confirma la selección. */
  confirmLabel?: string;
  /** Texto cuando el catálogo viene vacío. */
  emptyLabel?: string;
  size?: 'sm' | 'md' | 'lg';
  width?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Dropdown de selección múltiple (checkboxes) con confirmación interna.
 *
 * El borrador se edita dentro del menú; solo el botón "Confirmar" llama a
 * `onChange` y cierra. Cerrar por click afuera o Escape descarta el borrador
 * (Figma: "el botón interno del dropdown solo confirma la selección").
 */
export function MultiSelect({
  id,
  placeholder = 'Seleccionar',
  options,
  value,
  onChange,
  confirmLabel = 'Confirmar',
  emptyLabel = 'Sin registros',
  size = 'md',
  width = '100%',
  disabled = false,
  className,
}: MultiSelectProps) {
  const generatedId = useId();
  const triggerId = id || generatedId;
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(value);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });

  const hasSelection = value.length > 0;
  const triggerLabel = hasSelection ? `${placeholder} (${value.length})` : placeholder;

  const close = useCallback(() => setIsOpen(false), []);

  const open = useCallback(() => {
    setDraft(value);
    setIsOpen(true);
  }, [value]);

  const toggleOpen = useCallback(() => {
    if (disabled) return;
    if (isOpen) close();
    else open();
  }, [disabled, isOpen, close, open]);

  // Posicionar el menú debajo del trigger (arriba si no cabe).
  useEffect(() => {
    if (!isOpen || !triggerRef.current || !menuRef.current) return;

    const trigger = triggerRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const gap = 8;
    const safeArea = 16;

    let top = trigger.bottom + gap;
    if (top + menu.height + safeArea > window.innerHeight) {
      const above = trigger.top - menu.height - gap;
      top = above >= safeArea ? above : Math.max(safeArea, window.innerHeight - menu.height - safeArea);
    }

    let left = trigger.left;
    if (left + trigger.width + safeArea > window.innerWidth) {
      left = Math.max(safeArea, window.innerWidth - trigger.width - safeArea);
    }

    setPosition({ top, left, width: trigger.width });
  }, [isOpen]);

  // Cerrar (sin confirmar) al hacer click fuera.
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        close();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, close]);

  // Escape descarta el borrador.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  const toggleValue = (optionValue: string) => {
    setDraft((prev) =>
      prev.includes(optionValue) ? prev.filter((v) => v !== optionValue) : [...prev, optionValue],
    );
  };

  const handleConfirm = () => {
    onChange(draft);
    close();
  };

  const triggerClasses = [
    styles.trigger,
    styles[size],
    isOpen ? styles.triggerOpen : '',
    !hasSelection ? styles.placeholder : '',
    disabled ? styles.disabled : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const menu = isOpen
    ? createPortal(
        <div
          ref={menuRef}
          className={styles.menu}
          style={{ top: position.top, left: position.left, minWidth: position.width }}
          role="dialog"
          aria-label={placeholder}
        >
          <div className={styles.options}>
            {options.length === 0 ? (
              <p className={styles.empty}>{emptyLabel}</p>
            ) : (
              options.map((option) => {
                const checked = draft.includes(option.value);
                return (
                  <label key={option.value} className={styles.option}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={checked}
                      onChange={() => toggleValue(option.value)}
                    />
                    <span className={styles.optionLabel}>{option.label}</span>
                  </label>
                );
              })
            )}
          </div>

          <div className={styles.footer}>
            <button type="button" className={styles.confirm} onClick={handleConfirm}>
              {confirmLabel}
            </button>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className={styles.container} style={{ width }}>
      <button
        id={triggerId}
        ref={triggerRef}
        type="button"
        className={triggerClasses}
        onClick={toggleOpen}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span className={styles.triggerLabel}>{triggerLabel}</span>
        <svg
          className={styles.arrow}
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M1 1.5L6 6.5L11 1.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {menu}
    </div>
  );
}

MultiSelect.displayName = 'MultiSelect';
